import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Plus, Trash2, Settings, ChevronRight, Building2, X, Copy } from 'lucide-react';
import axios from 'axios';
import useConfirmDialog from '../hooks/useConfirmDialog';
import './EmployerClients.scss';

interface EmployerClient {
  id: number;
  employer_id: number;
  name: string;
  activity_sector?: string;
  descriptions?: string[];
}

interface CVTemplate {
  id: number;
  category: string;
  value: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EmployerClients: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const [clients, setClients] = useState<EmployerClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [activityOptions, setActivityOptions] = useState<CVTemplate[]>([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<EmployerClient | null>(null);
  const [modalForm, setModalForm] = useState({
    name: '',
    activity_sector: '',
    descriptions: [''] as string[]
  });
  const [saving, setSaving] = useState(false);

  // Gear menu
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const loadClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API_URL}/employer-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setClients(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
    const loadOptions = async () => {
      try {
        const res = await axios.get(`${API_URL}/cv-templates?grouped=true`);
        if (res.data?.data?.company_activity) {
          setActivityOptions(res.data.data.company_activity);
        }
      } catch {}
    };
    loadOptions();
  }, [loadClients]);

  // Close gear menu on outside click
  useEffect(() => {
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  if (isLoading) return <div className="employer-clients-page"><div className="loading">Kraunama...</div></div>;
  if (!isAuthenticated || user?.user_type !== 'employer') return <Navigate to="/" replace />;
  if (!user?.is_recruitment_agency) return <Navigate to="/" replace />;

  const openNewModal = () => {
    setEditingClient(null);
    setModalForm({ name: '', activity_sector: '', descriptions: [''] });
    setShowModal(true);
  };

  const openEditModal = (client: EmployerClient) => {
    setEditingClient(client);
    setModalForm({
      name: client.name,
      activity_sector: client.activity_sector || '',
      descriptions: client.descriptions && client.descriptions.length > 0 ? [...client.descriptions] : ['']
    });
    setShowModal(true);
    setOpenMenuId(null);
  };

  const handleDuplicate = (client: EmployerClient) => {
    setEditingClient(null);
    setModalForm({
      name: `${client.name} (kopija)`,
      activity_sector: client.activity_sector || '',
      descriptions: client.descriptions && client.descriptions.length > 0 ? [...client.descriptions] : ['']
    });
    setShowModal(true);
    setOpenMenuId(null);
  };

  const handleDelete = async (id: number) => {
    if (!await confirm('Ar tikrai norite ištrinti šį klientą?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/employer-clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(prev => prev.filter(c => c.id !== id));
    } catch {
      alert('Klaida trinant klientą');
    }
  };

  const handleSave = async () => {
    if (!modalForm.name.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        name: modalForm.name.trim(),
        activity_sector: modalForm.activity_sector || null,
        descriptions: modalForm.descriptions.filter(d => d.trim())
      };

      if (editingClient) {
        await axios.put(`${API_URL}/employer-clients/${editingClient.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/employer-clients`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowModal(false);
      loadClients();
    } catch {
      alert('Klaida išsaugant klientą');
    } finally {
      setSaving(false);
    }
  };

  const addDescription = () => {
    setModalForm(prev => ({ ...prev, descriptions: [...prev.descriptions, ''] }));
  };

  const updateDescription = (index: number, value: string) => {
    setModalForm(prev => ({
      ...prev,
      descriptions: prev.descriptions.map((d, i) => i === index ? value : d)
    }));
  };

  const removeDescription = (index: number) => {
    if (modalForm.descriptions.length <= 1) return;
    setModalForm(prev => ({
      ...prev,
      descriptions: prev.descriptions.filter((_, i) => i !== index)
    }));
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <div className="employer-clients-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="breadcrumbs">
          <Link to="/">Titulinis</Link>
          <ChevronRight size={14} />
          <span>Klientai</span>
        </div>

        <div className="header-row">
          <div className="tab-title">
            Klientai
            {clients.length > 0 && <span className="count">{clients.length}</span>}
          </div>
          <div className="filters-toolbar">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Kliento pavadinimas"
                value={searchFilter}
                onChange={e => setSearchFilter(e.target.value)}
                className="search-title-input"
              />
            </div>
            <button className="btn-new-client" onClick={openNewModal}>
              <Plus size={16} />
              <span>Naujas klientas</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Kraunami klientai...</div>
        ) : filteredClients.length === 0 && clients.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>Jūs dar neturite klientų</h3>
            <p>Pridėkite pirmąjį klientą, kad galėtumėte jį priskirti darbo skelbimams.</p>
            <button className="btn btn--primary" onClick={openNewModal}>
              <Plus size={20} /> Pridėti pirmąjį klientą
            </button>
          </div>
        ) : (
          <div className="clients-grid">
            {filteredClients.map(client => (
              <div key={client.id} className="client-card">
                <div className="client-card__actions">
                  <div className="gear-menu-wrapper">
                    <button
                      className="action-icon-btn"
                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === client.id ? null : client.id); }}
                    >
                      <Settings size={16} />
                    </button>
                    {openMenuId === client.id && (
                      <div className="gear-dropdown" onClick={e => e.stopPropagation()}>
                        <button onClick={() => openEditModal(client)}>
                          <Settings size={14} /> Redaguoti
                        </button>
                        <button onClick={() => handleDuplicate(client)}>
                          <Copy size={14} /> Kopijuoti
                        </button>
                      </div>
                    )}
                  </div>
                  <button className="action-icon-btn delete-btn" onClick={() => handleDelete(client.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <h3 className="client-card__name">{client.name}</h3>
                {client.activity_sector && (
                  <span className="client-card__sector">{client.activity_sector}</span>
                )}
                <p className="client-card__desc">
                  {client.descriptions && client.descriptions.length > 0
                    ? client.descriptions[0].substring(0, 200) + (client.descriptions[0].length > 200 ? '...' : '')
                    : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="client-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="client-modal" onClick={e => e.stopPropagation()}>
            <div className="client-modal__header">
              <h2>
                <Building2 size={20} />
                {editingClient ? editingClient.name : 'Naujas klientas'}
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="client-modal__body">
              <div className="form-row-inline">
                <div className="form-group">
                  <label>* Įmonės pavadinimas</label>
                  <input
                    type="text"
                    value={modalForm.name}
                    onChange={e => setModalForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Įmonės pavadinimas"
                  />
                </div>
                <div className="form-group">
                  <label>* Įmonės veiklos sritis</label>
                  <select
                    value={modalForm.activity_sector}
                    onChange={e => setModalForm(prev => ({ ...prev, activity_sector: e.target.value }))}
                  >
                    <option value="">Pasirinkite</option>
                    {activityOptions.map(opt => (
                      <option key={opt.id} value={opt.value}>{opt.value}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Įmonės veiklos aprašymai</label>
                {modalForm.descriptions.map((desc, i) => (
                  <div key={i} className="description-row">
                    <textarea
                      value={desc}
                      onChange={e => updateDescription(i, e.target.value)}
                      placeholder="Įmonės veiklos aprašymas..."
                      rows={4}
                    />
                    {modalForm.descriptions.length > 1 && (
                      <button className="remove-desc-btn" onClick={() => removeDescription(i)}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button className="add-desc-btn" onClick={addDescription}>
                  + Pridėti naują įmonės veiklos aprašymą
                </button>
              </div>
            </div>
            <div className="client-modal__footer">
              <button className="save-btn" onClick={handleSave} disabled={saving || !modalForm.name.trim()}>
                {saving ? 'Saugoma...' : '✓ IŠSAUGOTI'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerClients;
